/**
 * @typedef {Object} ErrorInfo
 * @property {string} key
 * @property {string} msg
 */

/**
 * @param {HTMLElement} container
 */
const clearErrors = function (container) {
  /**
   * @type {NodeListOf<HTMLElement>}
   */
  container
    .querySelectorAll(".n-form__error-msg")
    .forEach((x) => x.parentElement?.removeChild(x));

  container.querySelectorAll(".n-form .has-error").forEach((x) => {
    x.classList.remove("has-error");
  });
};

/**
 * @param {HTMLElement} container
 * @param {ErrorInfo[]} errors
 */
const showErrors = function (container, errors) {
  for (const error of errors) {
    /**
     * @type {HTMLElement|null}
     */
    const input =
      error.key === "token"
        ? container.querySelector(".n-form__recaptcha .g-recaptcha")
        : container.querySelector(`[name="${error.key}"]`);

    if (input) {
      input.classList.add("has-error");
      const errorMsg = document.createElement("div");
      errorMsg.className = "n-form__error-msg";
      errorMsg.textContent = error.msg;
      // @ts-ignore
      input.parentElement.appendChild(errorMsg);
    }
  }
};

/**
 * @param {HTMLFormElement} container
 * @param {string} url
 * @param {any} data
 * @returns {Promise<{success: boolean, errors: ErrorInfo[]}>}
 */
const saveData = async function (container, url, data) {
  const captchaRequired = container.dataset.captchaRequired === "true";

  if (captchaRequired && !data.token) {
    return {
      success: false,
      errors: [{ key: "token", msg: "Please select the checkbox." }],
    };
  }

  if ("token" in data) {
    delete data.token;
  }

  // SF expects the data to be in a form data object.
  const body = new URLSearchParams(data);

  const res = await fetch(url, {
    method: "POST",
    body: body,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    mode: "no-cors",
  });

  if (res.status === 200) {
    return { success: true, errors: [] };
  }

  if (res.status === 422) {
    const result = await res.json();
    return { success: false, errors: result.errors };
  }
  return { success: false, errors: [] };
};

/**
 * @param {HTMLFormElement}  form
 * @returns {[boolean, object]}
 */
const getData = function (form) {
  /**
   * @type {NodeListOf<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>}
   */
  const inputs = form.querySelectorAll("input,select,textarea");

  /**
   * @type {Object.<string, string>}>
   */
  let data = {};
  let isValid = true;

  inputs.forEach(function (input) {
    data[input.name] = input.value.trim();
  });

  return [isValid, data];
};

/**
 * @param {HTMLFormElement} container
 */
const initForm = function (container) {
  /** @type {HTMLFormElement} */
  // @ts-ignore
  const form = container.querySelector("form");

  let captchaToken = "";

  // @ts-ignore
  window.onDNSCaptchaSubmit = function (token) {
    captchaToken = token;
  };

  // @ts-ignore
  window.onDNSCaptchaExpired = function () {
    captchaToken = "";
  };

  /**
   * @type {NodeListOf<HTMLInputElement|HTMLTextAreaElement>}
   */
  const inputs = form.querySelectorAll("input[maxlength],textarea[maxlength]");

  inputs.forEach((input) => {
    const parent = input.parentElement;
    const help = document.createElement("div");
    help.className = "n-form__chars";
    help.innerHTML = [
      `Max <strong>${input.maxLength}</strong> characters`,
      `remaining: <strong class="is-chars">${input.maxLength}</strong>`,
    ].join(", ");

    // @ts-ignore
    parent.appendChild(help);

    input.addEventListener("keydown", function () {
      setTimeout(function () {
        const counter = help.querySelector(".is-chars");
        // @ts-ignore
        counter.textContent = input.maxLength - input.value.length;
      }, 1);
    });
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();

    if (container.dataset.status === "loading") {
      return;
    }

    const [_isValid, data] = getData(form);
    container.dataset.status = "loading";

    clearErrors(container);
    saveData(container, form.action, { ...data, token: captchaToken }).then(
      (res) => {
        if (res.errors.length > 0) {
          showErrors(container, res.errors);
          container.dataset.status = "errors";
          return;
        }
        container.dataset.status = "submitted";
      }
    );
  });
};

document.addEventListener("DOMContentLoaded", function () {
  /**
   * @type {NodeListOf<HTMLFormElement>}
   */
  const forms = document.querySelectorAll(".n-form");
  forms.forEach((x) => initForm(x));
});
